/**
 * Msg 560 · D.2 — Shared primitives + Topic form/delete modals.
 * Split out to keep individual file sizes small so `react-refresh/babel`
 * doesn't overflow the AST-traversal stack (default is generous but
 * files with many stateful components in dev-mode blow it).
 */
import React, { useEffect, useState } from "react";
import {
  Plus, Check, X as CloseIcon, Loader2, Trash2, AlertTriangle,
  Ship, MapPin, Building2, Hotel, Palmtree, Utensils, Landmark,
  Waves, Anchor, Folder, ChevronDown, ChevronUp,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { getTopicIcon as _getTopicIcon } from "./topicIcons";
import { useAtlasCategories } from "./atlasCategoriesCache";


// ─── Backend-mirror constants ───
export const TOPIC_CATEGORIES = [
  "destination", "category", "weather", "cruise_line", "cruise_ship",
  "resort", "hotel", "port", "airline", "restaurant", "attraction",
  "accommodation_type", "traveler_profile", "other",
  "cruise_line_group", "beach",
];
export const ATLAS_ENTITY_TYPES = [
  "cruise_ship", "cruise_line", "resort", "hotel", "destination",
  "other", "cruise_line_group", "beach", "port", "attraction", "restaurant",
];
export const ENTITY_TYPE_ICON = {
  cruise_ship: Ship, cruise_line: Anchor, cruise_line_group: Folder,
  resort: Palmtree, hotel: Hotel, destination: MapPin,
  beach: Waves, port: Anchor, attraction: Landmark,
  restaurant: Utensils, other: Building2,
};
export const humanize = (s) => (s || "").replace(/_/g, " ");


// ─── Primitives ───
export function Chip({ children, testId, tone = "neutral" }) {
  const tones = {
    neutral: "border-tb-navy/20 text-tb-navy/70 bg-white",
    navy:    "bg-tb-navy text-tb-gold-bright border-transparent",
    gold:    "bg-tb-gold-bright/15 text-tb-navy border-tb-gold-bright/50",
    ghost:   "border-tb-navy/10 text-tb-navy/50 bg-transparent",
  };
  return (
    <span data-testid={testId}
      className={`inline-flex items-center gap-1 h-5 px-2 rounded-full border font-hand text-[10px] uppercase tracking-widest ${tones[tone]}`}>
      {children}
      {/* D.2 a11y polish — sr-only comma so screen readers don't
          concatenate adjacent chips into one word. */}
      <span className="sr-only">, </span>
    </span>
  );
}

export function CountPill({ icon: Icon, count, label, testId }) {
  return (
    <span data-testid={testId}
      className="inline-flex items-center gap-1 font-hand text-[11px] text-tb-navy/70">
      <Icon className="w-3 h-3" aria-hidden="true" />
      {count || 0} {label}
      <span className="sr-only">, </span>
    </span>
  );
}

export function EntityIcon({ categoryId = null, type = null, category = null, className = "w-3.5 h-3.5" }) {
  // Msg 590 · Phase A — icons come from user-defined
  // `atlas_categories`. Subscribing to the cache here so ANY category
  // CRUD (create / rename / icon change / delete) triggers a live
  // icon refresh across every mounted tree row without a full page
  // reload. Legacy `type`/`category` props are accepted but silently
  // ignored — every topic's `entity_type` was nulled during the
  // Phase A migration so they carry no useful signal.
  useAtlasCategories();
  const I = _getTopicIcon(categoryId);
  return <I className={className} />;
}


// ─── Create/Edit topic modal ───
// Msg 564 · E.6/E.4 — Create modal is now Name + Aliases only. Category
// & entity_type are auto-inferred by the backend from the parent. Edit
// modal keeps the full form but tucks category/entity_type/parent
// behind an "Advanced" accordion collapsed by default. `focusField`
// forces the Advanced accordion open and auto-focuses that field —
// used by the Category chip on the focus header (E.4).
export function TopicFormModal({
  mode = "create", open, onClose, onSaved,
  initial = null, parentSlug = "", parentCandidates = [],
  focusField = null,
}) {
  const [name, setName]         = useState("");
  const [categoryId, setCategoryId] = useState(null);
  const [aliases, setAliases]   = useState("");
  const [parent, setParent]     = useState("__ROOT__");
  const [description, setDesc]  = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);
  // 2026-08-10 · Prod-orphan post-mortem — resolve the picked parent
  // slug into a canonical {slug, name} pair so the modal shows the
  // advisor EXACTLY where the new topic will land BEFORE they click
  // Save. `null` = still loading. `{missing: true}` = the picked
  // parent doesn't exist (should never happen from the picker, but
  // catches URL-slug corruption on the create-child flow).
  const [parentPreview, setParentPreview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setCategoryId(initial?.category_id || null);
    setAliases((initial?.aliases || []).join(", "));
    setParent(mode === "edit"
      ? (initial?.parent_slugs?.[0] || "__ROOT__")
      : (parentSlug || "__ROOT__"));
    setDesc(initial?.description || "");
    // Msg 596 · Bug fix — always default Advanced OPEN in edit mode so
    // the Category dropdown is visible without a second click. Previous
    // behaviour required `focusField` to be set (i.e. only the chip
    // pencil opened the panel; the top-right Edit button left Category
    // hidden). That was the "cannot edit category from focus page" bug.
    setAdvancedOpen(mode === "edit");
    setErr(null); setBusy(false);
  }, [open, mode, initial, parentSlug, focusField]);

  // 2026-08-10 · Prod-orphan post-mortem — whenever `parent` changes,
  // resolve the display name via /atlas/topics/{slug} so the modal
  // can render the confirmation preview. Skips the fetch for root
  // (`__ROOT__` / empty). If the backend returns 404, we surface
  // that as a `missing` state — the save will 400 anyway but the
  // user sees WHY up front.
  useEffect(() => {
    if (!open) return;
    if (!parent || parent === "__ROOT__") {
      setParentPreview({ root: true });
      return;
    }
    setParentPreview(null);
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/atlas/topics/${encodeURIComponent(parent)}`);
        if (cancelled) return;
        const t = r.data?.topic;
        if (t?.slug) {
          setParentPreview({ slug: t.slug, name: t.name || t.slug });
        } else {
          setParentPreview({ missing: true, slug: parent });
        }
      } catch {
        if (!cancelled) setParentPreview({ missing: true, slug: parent });
      }
    })();
    return () => { cancelled = true; };
  }, [parent, open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!name.trim()) { setErr("Name is required."); return; }
    setBusy(true);
    try {
      const aliasList = aliases.split(",").map((s) => s.trim()).filter(Boolean);
      // Msg 590 · Phase A — form now sends `category_id` instead of
      // the retired `category`/`entity_type` pair. `null` clears the
      // tag; omitting the key would leave the existing value.
      const body = mode === "create"
        ? {
            name: name.trim(),
            aliases: aliasList,
            description: description.trim() || undefined,
            parent_slug: parent === "__ROOT__" ? "" : parent,
            category_id: categoryId,
          }
        : {
            name: name.trim(),
            aliases: aliasList,
            description: description.trim() || undefined,
            parent_slug: parent,
            category_id: categoryId,
          };
      if (mode === "create") {
        const r = await api.post("/atlas/topics", body);
        const t = r.data?.topic || {};
        toast.success(r.data?.already_existed ? "Topic already exists — opening it." : "Topic created.");
        onSaved?.(t);
      } else {
        const r = await api.put(`/atlas/topics/${initial.slug}`, body);
        toast.success("Topic updated.");
        onSaved?.(r.data?.topic || initial);
      }
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = ex?.response?.data?.detail;
      if (status === 409) setErr("A topic with this name already exists.");
      else if (status === 400) setErr(formatApiErrorDetail(detail) || "Invalid input.");
      else setErr(formatApiErrorDetail(detail) || "Save failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-start md:items-center justify-center overflow-y-auto p-4"
         data-testid={`topic-form-modal-${mode}`}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit}
        className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-tb-navy/15 my-6">
        <TopicFormHeader mode={mode} parentSlug={parentSlug} onClose={onClose} />
        <div className="p-6 space-y-4">
          <TopicFormFields
            name={name} setName={setName}
            categoryId={categoryId} setCategoryId={setCategoryId}
            aliases={aliases} setAliases={setAliases}
            description={description} setDesc={setDesc}
            mode={mode}
            parent={parent} setParent={setParent}
            parentCandidates={parentCandidates}
            advancedOpen={advancedOpen}
            setAdvancedOpen={setAdvancedOpen}
            initial={initial}
            parentPreview={parentPreview}
            onTemplateApplied={() => onSaved?.(initial)}
          />
          {err && (
            <div data-testid="topic-form-error"
              className="rounded-lg border border-red-300 bg-red-50 p-2.5 font-hand text-red-800 text-xs">
              {err}
            </div>
          )}
        </div>
        <TopicFormFooter busy={busy} mode={mode} onCancel={onClose} />
      </form>
    </div>
  );
}


function TopicFormHeader({ mode, parentSlug, onClose }) {
  const title = mode === "create"
    ? (parentSlug ? "New sub-topic" : "New category")
    : "Edit topic";
  return (
    <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-tb-navy/10">
      <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
        {title}
      </h2>
      <button type="button" onClick={onClose}
        data-testid="topic-form-close"
        className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
}


function TopicFormFields({
  name, setName, categoryId, setCategoryId,
  aliases, setAliases, description, setDesc,
  mode, parent, setParent, parentCandidates,
  advancedOpen, setAdvancedOpen,
  initial = null, parentPreview = null, onTemplateApplied,
}) {
  const categories = useAtlasCategories();
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const canApplyTemplate = mode === "edit"
    && initial && !initial.template_key
    && !initial.template_scaffold;

  const runApply = async (tplKey) => {
    if (!initial?.slug) return;
    setApplyingTemplate(true);
    try {
      const r = await api.post(
        `/atlas/topics/${initial.slug}/apply-template?template=${encodeURIComponent(tplKey)}`);
      const created = (r.data?.created || []).length;
      const skipped = r.data?.skipped_count || 0;
      if (created > 0) {
        toast.success(`Applied template — added ${created} folder${created === 1 ? "" : "s"}` +
          (skipped ? ` (${skipped} already existed)` : "."));
      } else {
        toast.info(`Template already applied (${skipped} folders present).`);
      }
      onTemplateApplied?.(tplKey);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || "Failed to apply template.");
    } finally {
      setApplyingTemplate(false);
    }
  };

  return (
    <>
      <label className="block">
        <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)}
          data-testid="topic-form-name" autoFocus maxLength={80}
          className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
      </label>
      {/* 2026-08-10 · Prod-orphan post-mortem — inline parent preview.
          Renders in BOTH create + edit modes. On create the parent is
          fixed (props); on edit the parent field lives in the
          Advanced accordion and the preview follows any switch. */}
      {parentPreview?.root && (
        <div
          data-testid="topic-form-parent-preview-root"
          className="rounded-lg border border-tb-navy/15 bg-tb-navy/[0.03] px-3 py-2 font-hand text-[11px] text-tb-navy/70"
        >
          Will be created at the <b>top level</b> (no parent).
        </div>
      )}
      {parentPreview && !parentPreview.root && !parentPreview.missing && (
        <div
          data-testid="topic-form-parent-preview"
          className="rounded-lg border border-tb-navy/15 bg-tb-navy/[0.03] px-3 py-2 font-hand text-[11px] text-tb-navy/80"
        >
          Will be created under: <b>{parentPreview.name}</b>
          <span className="ml-1 text-tb-navy/50">(slug: {parentPreview.slug})</span>
        </div>
      )}
      {parentPreview?.missing && (
        <div
          data-testid="topic-form-parent-preview-missing"
          className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 font-hand text-[11px] text-red-800"
        >
          Parent <b>{parentPreview.slug}</b> was not found. Saving will fail — pick
          a real parent (or set to the top level) before continuing.
        </div>
      )}
      {parentPreview === null && parent && parent !== "__ROOT__" && (
        <div className="font-hand text-[11px] text-tb-navy/40 px-1"
             data-testid="topic-form-parent-preview-loading">
          Resolving parent…
        </div>
      )}
      {/* Msg 574 · UX-Cleanup — Aliases & Description live in Edit only.
          Create flow keeps a single Name field so the advisor isn't
          confronted with entity_type / slug / description / category
          when the parent already tells us where the topic lives. */}
      {mode === "edit" && (
        <>
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
              Aliases (comma-separated)
            </span>
            <input value={aliases} onChange={(e) => setAliases(e.target.value)}
              placeholder="e.g. Wonder OTS, Wonder ship"
              data-testid="topic-form-aliases"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
              Description (optional)
            </span>
            <textarea value={description} onChange={(e) => setDesc(e.target.value)}
              rows={3} data-testid="topic-form-description"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
        </>
      )}

      {mode === "edit" && (
        <div className="border border-tb-navy/15 rounded-xl overflow-hidden bg-white">
          <button type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            data-testid="topic-form-advanced-toggle"
            aria-expanded={advancedOpen}
            className="w-full flex items-center justify-between px-3 py-2 hover:bg-tb-navy/[0.03] transition"
          >
            <span className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/70">
              Advanced (category · parent · template)
            </span>
            {advancedOpen
              ? <ChevronUp className="w-4 h-4 text-tb-navy/60" />
              : <ChevronDown className="w-4 h-4 text-tb-navy/60" />}
          </button>
          {advancedOpen && (
            <div className="px-3 pb-3 pt-1 space-y-3" data-testid="topic-form-advanced-panel">
              <label className="block">
                <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Category</span>
                <select value={categoryId || ""} onChange={(e) => setCategoryId(e.target.value || null)}
                  data-testid="topic-form-category-id"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
                  <option value="">— None —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {categories.length === 0 && (
                  <span className="mt-1 block text-[10px] font-hand text-tb-navy/50">
                    No categories yet — create some in <b>Taxonomy admin → Categories</b>.
                  </span>
                )}
              </label>
              <label className="block">
                <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Parent</span>
                <select value={parent} onChange={(e) => setParent(e.target.value)}
                  data-testid="topic-form-parent"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
                  <option value="__ROOT__">— No parent (root topic) —</option>
                  {parentCandidates.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.name} ({p.slug})</option>
                  ))}
                </select>
              </label>

              {mode === "edit" && initial?.template_key && (
                <div data-testid="topic-form-template-key"
                  className="rounded-lg border border-tb-gold-bright/40 bg-tb-gold-bright/10 p-2.5 font-hand text-[11px] text-tb-navy/80">
                  <b>Template:</b> {humanize(initial.template_key)}
                  <span className="ml-1 text-tb-navy/50">— sub-folders auto-scaffold under this topic.</span>
                </div>
              )}

              {canApplyTemplate && (
                <div data-testid="topic-form-apply-template"
                  className="rounded-lg border border-tb-navy/15 bg-tb-navy/[0.02] p-3 space-y-2">
                  <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
                    Apply a template
                  </div>
                  <p className="font-hand text-[11px] text-tb-navy/60">
                    Auto-create standard sub-folders under this topic. Idempotent — existing
                    sub-folders are kept.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button type="button" onClick={() => runApply("country")}
                      disabled={applyingTemplate}
                      data-testid="topic-form-apply-country"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50">
                      {applyingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Country (5 folders)
                    </button>
                    <button type="button" onClick={() => runApply("cruise_line")}
                      disabled={applyingTemplate}
                      data-testid="topic-form-apply-cruise-line"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5 disabled:opacity-50">
                      {applyingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Cruise Line (3 folders)
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}


function TopicFormFooter({ busy, mode, onCancel }) {
  return (
    <div className="px-6 pb-5 pt-1 flex items-center justify-end gap-2">
      <button type="button" onClick={onCancel} disabled={busy}
        data-testid="topic-form-cancel"
        className="h-10 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
        Cancel
      </button>
      <button type="submit" disabled={busy}
        data-testid="topic-form-submit"
        className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" />
              : mode === "create" ? <Plus className="w-4 h-4" /> : <Check className="w-4 h-4" />}
        {mode === "create" ? "Add" : "Save changes"}
      </button>
    </div>
  );
}


// ─── Delete-strategy modal ───
export function DeleteStrategyModal({ open, topic, childCount, factCount, onClose, onDeleted }) {
  const [strategy, setStrategy] = useState("block");
  const [busy, setBusy] = useState(false);
  // Msg 589 · P0 · confirmation friction for large cascade deletes.
  const [preview, setPreview] = useState(null);   // { descendant_count, descendant_preview[] }
  const [typedName, setTypedName] = useState("");
  useEffect(() => {
    if (!open) return;
    setStrategy(childCount > 0 || factCount > 0 ? "orphan" : "block");
    setBusy(false);
    setPreview(null);
    setTypedName("");
  }, [open, childCount, factCount]);
  if (!open || !topic) return null;

  // Msg 589 — for large cascades the backend returns 428 with a preview;
  // client must re-POST with `confirm_cascade=true` AFTER user types the
  // topic name to confirm.
  const LARGE_CASCADE_THRESHOLD = 20;
  const isLargeCascade = strategy === "cascade" && (preview?.descendant_count ?? 0) >= LARGE_CASCADE_THRESHOLD;
  // 2026-08-06 — simplified confirmation. Advisors just type `yes`
  // (case-insensitive, whitespace-trimmed) to enable Confirm.
  const nameConfirmed = typedName.trim().toLowerCase() === "yes";

  const submit = async () => {
    // Msg 606 · P0 · WRONG-FOLDER-DELETE guard — typed-name check for
    // EVERY delete, not just large cascades. The backend also
    // validates via `expected_name=` (409 on mismatch) so a UI wiring
    // bug (stale slug closure, wrong-row map, etc.) can never cost
    // the wrong folder.
    if (!nameConfirmed) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({ strategy });
      if (strategy === "cascade" && preview) params.set("confirm_cascade", "true");
      // 2026-08-06 — backend now expects `yes` (case-insensitive)
      // as the confirmation payload; the folder name itself is no
      // longer accepted. Query-param key preserved for wire compat.
      params.set("expected_name", "yes");
      await api.delete(`/atlas/topics/${topic.slug}?${params.toString()}`);
      toast.success(`Deleted "${topic.name}" (${strategy}).`);
      onDeleted?.();
    } catch (e) {
      const s = e?.response?.status;
      const data = e?.response?.data?.detail;
      if (s === 428 && data && data.requires_confirm_cascade) {
        // Show preview + require typing to confirm.
        setPreview({
          descendant_count: data.descendant_count,
          descendant_preview: data.descendant_preview || [],
        });
      } else if (s === 409 && data && data.actual_name !== undefined) {
        // 2026-08-06 backend confirmation guard tripped — user
        // sent something other than "yes" as expected_name.
        toast.error(`Confirmation missing — type "yes" (case-insensitive) to delete "${data.actual_name}". No delete happened.`);
      } else if (s === 409) {
        toast.error("Topic still has children or facts — try orphan or cascade.");
      } else {
        toast.error(formatApiErrorDetail(data) || "Delete failed.");
      }
    } finally { setBusy(false); }
  };

  const canSubmit = !busy && nameConfirmed && (!isLargeCascade || nameConfirmed);

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="delete-topic-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-6 pt-5 pb-3 border-b border-tb-navy/10 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
            Delete "{topic.name}"?
          </h2>
        </div>
        <div className="p-6 space-y-3">
          <p className="font-hand text-tb-navy/70 text-sm">
            This topic has <b>{childCount}</b> child topic{childCount === 1 ? "" : "s"} and{" "}
            <b>{factCount}</b> canonical fact{factCount === 1 ? "" : "s"}. Choose how to handle them:
          </p>
          <StrategyOption value="block" strategy={strategy} setStrategy={setStrategy}
            title="Block" desc="Refuse the delete if any children or facts exist. Safest option." />
          <StrategyOption value="orphan" strategy={strategy} setStrategy={setStrategy}
            title="Orphan (reparent)" desc="Move direct children up to this topic's parent. Facts stay attached and are deleted with it." />
          <StrategyOption value="cascade" strategy={strategy} setStrategy={setStrategy}
            title="Cascade" desc="Delete this topic AND every descendant AND their facts. Cannot be undone." danger />

          {/* Msg 606 · P0 · WRONG-FOLDER-DELETE guard — typed-name
              confirmation required for EVERY delete (single, orphan,
              cascade). Shows the exact slug + descendant count so
              there is zero ambiguity about which folder is going
              away. The backend also validates via `expected_name=`;
              a mismatch triggers a 409 with both names surfaced. */}
          <div className="mt-3 rounded-xl border border-tb-navy/25 bg-tb-navy/[0.04] p-3 space-y-2"
               data-testid="delete-topic-confirm-block">
            <p className="text-tb-navy text-sm font-hand">
              About to delete: <b>{topic.name}</b>{" "}
              <span className="text-tb-navy/50 text-xs">
                ({topic.slug})
              </span>
            </p>
            <p className="text-tb-navy/70 text-xs font-hand">
              Direct children: <b>{childCount}</b>
              {" · "}Canonical facts: <b>{factCount}</b>
              {strategy === "cascade" && preview?.descendant_count !== undefined && (
                <> {" · "}Total descendants that will be destroyed:{" "}
                <b className="text-red-700">{preview.descendant_count}</b></>
              )}
            </p>
            <label className="block text-xs font-hand text-tb-navy/80 pt-1">
              Type <b>yes</b> to confirm deleting "<b>{topic.name}</b>":
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              autoFocus
              data-testid="delete-topic-confirm-name-input"
              placeholder="yes"
              className={`w-full rounded border px-2 py-1 text-sm outline-none ${
                nameConfirmed
                  ? "border-emerald-500 focus:border-emerald-600 bg-emerald-50/40"
                  : "border-tb-navy/25 focus:border-tb-navy"
              }`}
            />
            {typedName && !nameConfirmed && (
              <p className="text-[11px] font-hand text-red-600">
                Type <b>yes</b> (case-insensitive) to enable the delete button.
              </p>
            )}
          </div>

          {/* Msg 589 — large cascade preview (shown after 428) */}
          {preview && isLargeCascade && (
            <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 space-y-2"
                 data-testid="delete-topic-cascade-preview">
              <p className="text-red-700 text-sm font-hand">
                <b>Cascade will destroy {preview.descendant_count} descendant topic{preview.descendant_count === 1 ? "" : "s"}</b> in addition to "{topic.name}".
                This cannot be undone.
              </p>
              <div className="max-h-32 overflow-y-auto text-[11px] font-hand text-red-800 space-y-0.5">
                {preview.descendant_preview.slice(0, 15).map((d) => (
                  <div key={d.slug} className="truncate">
                    • {d.name} <span className="text-red-500/70">({d.slug})</span>
                  </div>
                ))}
                {preview.descendant_preview.length > 15 && (
                  <div className="text-red-500/70">
                    …and {preview.descendant_count - 15} more.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 pb-5 pt-1 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="delete-topic-cancel"
            className="h-10 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit}
            data-testid="delete-topic-confirm"
            className={`inline-flex items-center gap-2 h-10 px-5 rounded-full font-hand text-sm text-white disabled:opacity-40 ${
              strategy === "cascade" ? "bg-red-700" : "bg-tb-navy"
            }`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {nameConfirmed ? `Delete (${strategy})` : "Type yes to enable"}
          </button>
        </div>
      </div>
    </div>
  );
}


function StrategyOption({ value, strategy, setStrategy, title, desc, danger = false }) {
  const active = strategy === value;
  const activeStyle = danger ? "border-red-500 bg-red-50" : "border-tb-navy bg-tb-navy/5";
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
        active ? activeStyle : "border-tb-navy/15 hover:bg-tb-navy/[0.02]"
      }`}
      data-testid={`delete-strategy-${value}`}
    >
      <input type="radio" name="strategy" value={value}
        checked={active} onChange={() => setStrategy(value)}
        className="mt-1 accent-tb-navy" />
      <div>
        <div className={`font-hand text-sm font-semibold ${danger ? "text-red-700" : "text-tb-navy"}`}>{title}</div>
        <div className="font-hand text-[11px] text-tb-navy/60 mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
